import {addBackgroundThemeImage} from "@frontend/group/modules/additional-fields";
import {
  getProductImageFields, uploadProductImage,
  uploadProductImageAndDraw,
  handleProductImageUpload
} from "@frontend/group/modules/add-product-image/add-product-image-service";
import {editorControlEventBroker, EVENTS} from "@frontend/group/modules/editor-control-popup/event-broker";
import {FIELD_TYPE_PRODUCT_IMAGE, FIELD_TYPE_BACKGROND_THEME_IMAGE} from "@frontend/constants/type-fields-of-template";
import {ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID} from "@frontend/constants/templates-field-prefixes";
import {importHyperlinksEventBroker} from '@frontend/group/modules/import-hyperlinks/event-broker';
import store from "@frontend/store";
import {MUTATIONS} from "@frontend/store/file-browser-product-selector";
import {ShowPageScopeCollector} from "@frontend/group/modules/scope-collectors/show-page-collector";

const getScopePage = () => ShowPageScopeCollector.get([
  'handleObjectMoving',
  'replaceBackgroundImage',
  'reorderObjectCanvas',
  'canvas_data',
  'replaceImageForAdditionalBackgroundThemeImage',
  'updateLayoutChanges',
  'setAdditionalBackgroundImage'
]);

const DRAG_OVER_BORDER = '2px dashed #1c7964';
const DEFAULT_BORDER = 'none';

class TemplateDragDropService {
  constructor(previews, template_data) {
    this.previews = previews;
    this.template_data = template_data;
    this.currentFile = null;
    this.currentTemplate = null;
    this.isReplacedOnce = false;
    this.isConfirmedOnce = false;
    this.files = null;
  }

  initializeTemplates(templates) {
    templates.forEach(template => {
      this.attachDragHandlers(template);
    });
  }

  attachDragHandlers(template) {
    template.addEventListener('dragover', (e) => this.handleDragOver(e, template));
    template.addEventListener('dragleave', (e) => this.handleDragLeave(e, template));
    template.addEventListener('drop', (e) => this.handleDrop(e, template));
  }

  handleDragOver(e, template) {
    e.preventDefault();
    e.stopPropagation();
    template.style.border = DRAG_OVER_BORDER;
  }

  handleDragLeave(e, template) {
    e.preventDefault();
    e.stopPropagation();
    template.style.border = DEFAULT_BORDER;
  }

  async handleDrop(e, template) {
    e.preventDefault();
    e.stopPropagation();
    template.style.border = DEFAULT_BORDER;
    this.isReplacedOnce = false;
    this.isConfirmedOnce = false;

    const files = e.dataTransfer.files;
    if (!files.length) return;

    const instanceId = $(template).closest('[data-instance]').data('instance')
    if (!instanceId) {
      toastr.error('Could not determine template instance');
      return;
    }

    // Check for JSON files first
    const jsonFiles = this.filterJsonFiles(files);
    if (jsonFiles.length > 0) {
      // Handle JSON files - either single or multiple
      if (jsonFiles.length === 1) {
        this.handleJsonDrop(jsonFiles[0], instanceId);
      } else {

        this.handleMultipleJsonDrop(jsonFiles, instanceId);
      }
      return;
    }

    // Handle image files
    this.files = files;
    for (const firstFile of files) {
      if (this.isValidImageFile(firstFile)) {
        await this.handleImageFile(firstFile, template, instanceId, e);
      } else {
        toastr.error('Invalid file type. Please use jpg, png, webp, or json files.');
      }
    }

    $('.loadingOverlay').fadeOut();
  }

  handleMultipleJsonDrop(files, instanceId) {
    // Store the files in global variables
    window.droppedJsonFiles = Array.from(files);
    window.droppedInstanceId = instanceId;
    window.bulkJsonUpload = true;

    toastr.info(`Processing ${files.length} JSON files. Files will be applied to templates in order.`);

    // Fire the init event to open the modal
    importHyperlinksEventBroker.fire(EVENTS.INIT);
  }

  handleJsonDrop(file, instanceId) {
    // Store the file in global variables
    window.droppedJsonFiles = [file];
    window.droppedInstanceId = instanceId;
    window.bulkJsonUpload = false;

    // Fire the init event to open the modal
    importHyperlinksEventBroker.fire(EVENTS.INIT);
  }

  filterJsonFiles(files) {
    return Array.from(files).filter(file =>
      file.type === 'application/json' || file.name.toLowerCase().endsWith('.json')
    );
  }

  isValidImageFile(file) {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    return validTypes.includes(file.type);
  }

  isPlaceholder(object, instanceId, type = null) {
    const { canvas_data } = getScopePage();
    const templateField = canvas_data[instanceId]?.templateInstance?.fields?.find(field => field.id === object?.fieldId);
    const fileName = templateField?.options?.filename;

    // check if image is a placeholder
    // placeholders are uploaded in /uploads directory
    if (type === 'background' && object?.src?.includes('uploads')) {
      return true;
    }

    // check if product image is a placeholder
    if (type === 'productImage' && fileName) {
      const templateFieldName = fileName?.split('/').pop();
      const canvasObjectName = object.fileName?.split('/').pop();

      //if different = product image has been replaced at least once
      if (templateFieldName !== canvasObjectName) {
        return false;
      }

      return true;
    }

    return false;
  }

  async handleImageFile(file, template, instanceId, dropEvent) {
    this.currentFile = file;
    this.currentTemplate = template;
    
    const targetPreview = this.previews.find(preview => preview.id === instanceId);
    // Check if dropped on product image
    const replaceProductImage = this.getObjectOnWhichDrop({
      dropEvent,
      targetPreview,
      template
    });

    if (replaceProductImage) {
      $('.loadingOverlay').fadeIn();
      $('.loadingOverlay-text').html('Replacing Product Images...');
      // check if product image is a static placeholder
      if (this.isPlaceholder(replaceProductImage, instanceId, 'productImage') && window.isBannerBuilder) {
        for (const preview of this.previews) {
          const foundObject = preview.canvas.getObjects().find((obj) =>
            obj.fieldType === FIELD_TYPE_PRODUCT_IMAGE &&
            obj.element_id === replaceProductImage.element_id
          );

          if (foundObject) {
            await this.handleProductImageDrop({file, targetPreview: preview, replaceProductImage: foundObject});
          }
        }
        return;
      }
      
      await this.handleProductImageDrop({file, targetPreview, replaceProductImage});

      return;
    }

    const replaceBackgroundImage = this.getObjectOnWhichDrop({
      dropEvent,
      targetPreview,
      template,
      type: 'background'
    });

    if (replaceBackgroundImage && !this.isReplacedOnce) {
      $('.loadingOverlay').fadeIn();
      $('.loadingOverlay-text').html('Replacing Images...');
      this.isConfirmedOnce = true;
      if (this.isPlaceholder(replaceBackgroundImage, instanceId, 'background') && window.isBannerBuilder) {
        for (const preview of this.previews) {
          const foundObject = preview.canvas.getObjects().find((obj) =>
            obj.fieldType === FIELD_TYPE_BACKGROND_THEME_IMAGE &&
            obj.element_id === replaceBackgroundImage.element_id
          );

          if (foundObject) {
            await this.handleBackgroundImageDrop(this.currentFile, preview.id, null, replaceBackgroundImage);
          }
        }

        this.isReplacedOnce = true;
        return;
      }

      // if image is already replace, skip to creating a new background image
      if (!this.isReplacedOnce) {
        await this.handleBackgroundImageDrop(this.currentFile, instanceId, null, replaceBackgroundImage);
        this.isReplacedOnce = true;
      }
      
      return;
    }

    // if modal is shown once, skip for multiple files
    if (this.isConfirmedOnce) {
      await this.handleBackgroundImageDrop(this.currentFile, instanceId);
      return;
    }
    // Show image type selection modal
    await this.showImageTypeModal(instanceId, targetPreview);
  }

  async showImageTypeModal(instanceId, targetPreview) {
    // added promise to support multi image replace / creating
    return new Promise((resolve) => {
      const modal = $('#image-type-selection-modal');

      if (window.isBannerBuilder) {
        document.querySelector('#showApplyToAllTemplates').classList.remove('d-none');
      }
      modal.modal('show');

      $('#confirm-image-type').off('click');

      $('#confirm-image-type').one('click', async () => {
        await this.handleImageTypeConfirmation(instanceId, targetPreview, modal);
        resolve();
      });

      modal.one('hidden.bs.modal', () => {
        $('#confirm-image-type').off('click');
        this.currentFile = null;
        this.currentTemplate = null;
        resolve();
      });
    });
  }

  async handleImageTypeConfirmation(instanceId, targetPreview, modal) {
    const confirmButton = $('#confirm-image-type');
    let applyToAll = false;
    this.setButtonLoading(confirmButton, true);

    const selectedType = document.querySelector('input[name="imageType"]:checked').value;

    if (window.isBannerBuilder) {
      applyToAll = document.querySelector('#applyToAllTemplates').checked;

      if (applyToAll) {
        try {
          if (selectedType === 'background') {
            this.isConfirmedOnce = true;
            for (const preview of this.previews) {
              await this.handleBackgroundImageDrop(this.currentFile, preview.id, preview?.template);
            }
          } else {
            const result = await handleProductImageUpload(this.currentFile, instanceId); // upload once for multi templates
            for (const preview of this.previews) {
                await this.handleProductImageDrop({file: this.currentFile, targetPreview: preview, recentImage: result});          
            }
          }
        } finally {
          if (this.currentFile?.name === this.files[this.files.length -1]?.name) { // close modal after the last file is added / replaced
            this.setButtonLoading(confirmButton, false);
            modal.modal('hide');
          }
        }
        return;
      }
    }
    try {
      if (selectedType === 'background') {
        this.isConfirmedOnce = true;
        await this.handleBackgroundImageDrop(this.currentFile, instanceId);
      } else {
        await this.handleProductImageDrop({file: this.currentFile, targetPreview});
      }
    } finally {
      if (this.currentFile?.name === this.files[this.files.length -1]?.name) { // close modal after the last file is added / replaced
          this.setButtonLoading(confirmButton, false);
            await new Promise(resolve => {
              modal.one('hidden.bs.modal', resolve);
              modal.modal('hide');
            });
          }
    }
  }

  async handleBackgroundImageDrop(file, instanceId, customTemplateData = null, existingImage = null) {
    let { id: template_id, customer_id } = this.template_data;

    if (customTemplateData) {
      ({ id: template_id, customer_id } = customTemplateData);
    } 

    const theme_id = $('#theme').val();
    const formData = new FormData();
    
    formData.append('customer_id', customer_id);
    formData.append('template_id', template_id);
    formData.append('theme_id', theme_id);
    formData.append('file_1', file);
    formData.append('filename_1', file.name);

    try {
      const data = await axios.post('/banner/upload_bk_image', formData);
      toastr.success('Image saved successfully');
      const name = data.data[0].path;
      const uploadedImageUrl = `/share?file=${name}`;
      const currentPreview = this.previews.find(preview => preview.id === instanceId);
      const options = {
        currentInstanceId: instanceId,
        currentPreview,
        additional_background_images: uploadedImageUrl
      };

      //if existingImage replace the image instead of adding a new one
      if (existingImage) {
        const { replaceBackgroundImage, 
          handleObjectMoving, 
          reorderObjectCanvas, 
          canvas_data, 
          replaceImageForAdditionalBackgroundThemeImage, 
          updateLayoutChanges, 
          setAdditionalBackgroundImage } = getScopePage();

        const isAdditionalBackgroundThemeImage = existingImage.id
                      && existingImage.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID);
        if (isAdditionalBackgroundThemeImage) {
          await replaceImageForAdditionalBackgroundThemeImage(
                          existingImage,
                          { path: uploadedImageUrl },
                          currentPreview,
                          updateLayoutChanges,
                          instanceId,
                          setAdditionalBackgroundImage,
                          handleObjectMoving,
                          existingImage
                        );
          return;
        }

        await replaceBackgroundImage(uploadedImageUrl, existingImage.id, instanceId, false)
        .then((object) => {
            reorderObjectCanvas(currentPreview.canvas)
            handleObjectMoving({
              target: object,
            });
            toastr.success('Image replaced successfully')
          });
        return;
      }

      await addBackgroundThemeImage(options);
    } catch (error) {
      toastr.error('Failed to upload background image');
      console.error(error);
    } finally {
      const modal = $('#image-type-selection-modal');
      if (this.currentFile?.name === this.files[this.files.length -1]?.name) { // close modal after the last file is added / replaced
        const confirmButton = $('#confirm-image-type');
        this.setButtonLoading(confirmButton, false);
        modal.modal('hide');
      }
    }
  }

  setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.prop('disabled', true);
      button.html('<i class="fa fa-spinner fa-spin"></i> Processing...');
    } else {
      button.prop('disabled', false);
      button.html('Confirm');
    }
  }

  async handleProductImageDrop({file, targetPreview, replaceProductImage = null, recentImage = null}) {
    const canvas = targetPreview.canvas;

    //create new product image 
    if (!replaceProductImage){
      const existingImages = getProductImageFields(targetPreview);

      await uploadProductImageAndDraw({
        file,
        canvas,
        selectedLayoutId: targetPreview.id,
        existingImages,
        recentImage: recentImage
      });
      return;
    }

    //if replaceTargetId replace existing product image
    const uploadResponse = await uploadProductImage(file);
    const filename = uploadResponse.filenames[0];

    //set active element on which drop image no need when replace from sidebar (exit templateFieldId property)
    if (!replaceProductImage.templateFieldId) {
      canvas.setActiveObject(replaceProductImage);
    }
    const oldFilename = replaceProductImage.fileName;

    await editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
      action: 'productImage', value: filename.split('.')[0]
    });

    //need to remove swaped image from picked items in store to correctly work [[syncPickedItemsWithSelectedFileNames]]
    if (oldFilename) {
      const pickedItems = store.getters['fileBrowserProductSelector/pickedItems'];
      const indexInPicked = pickedItems.findIndex(item => item.fileId === oldFilename);
      if (indexInPicked !== -1) {
        pickedItems.splice(indexInPicked, 1);
        store.commit(`fileBrowserProductSelector/${MUTATIONS.SET_PICKED_ITEMS}`, pickedItems);
      }
    }
    
    toastr.success('Image replaced successfully');
  }

  getRealCanvasCoordinates(dropEvent, targetPreview, template) {
    const canvas = targetPreview.canvas;

    // Get canvas element bounding rect
    const rect = template.getBoundingClientRect();

    // Calculate mouse position relative to canvas element
    const mouseX = dropEvent.clientX - rect.left;
    const mouseY = dropEvent.clientY - rect.top;

    // Get the actual canvas dimensions
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Get the displayed dimensions
    const displayWidth = rect.width;
    const displayHeight = rect.height;

    // Calculate scale factors
    const scaleX = canvasWidth / displayWidth;
    const scaleY = canvasHeight / displayHeight;

    // Convert to real canvas coordinates
    const canvasX = mouseX * scaleX;
    const canvasY = mouseY * scaleY;

    const zoom = canvas.getZoom();

    // Final coordinates accounting for zoom and pan
    const finalX = canvasX / zoom;
    const finalY = canvasY / zoom;

    return {
      positionX: finalX,
      positionY: finalY,
    };
  }

  isPointInsideObject(point, obj) {
    const {positionX, positionY} = point;
    const coords = obj.aCoords;

    if (!coords || !coords.tl || !coords.br) {
      return false;
    }

    // Check if point is within the bounding box
    return positionX >= coords.tl.x &&
      positionX <= coords.br.x &&
      positionY >= coords.tl.y &&
      positionY <= coords.br.y;
  }

  getObjectOnWhichDrop({dropEvent, targetPreview, template, type = null}) {
    const canvas = targetPreview.canvas;

    const dropCoordinates = this.getRealCanvasCoordinates(dropEvent, targetPreview, template);

    if (type === 'background') {
      const foundObjects = canvas.getObjects().filter((obj) =>
        obj.fieldType === FIELD_TYPE_BACKGROND_THEME_IMAGE &&
        this.isPointInsideObject(dropCoordinates, obj)
      );

      const foundObject = foundObjects[foundObjects.length - 1]; // pick the top image if multiple images found

      return foundObject || null;
    }

    const foundObject = canvas.getObjects().find((obj) =>
      obj.fieldType === FIELD_TYPE_PRODUCT_IMAGE &&
      this.isPointInsideObject(dropCoordinates, obj)
    );

    return foundObject || null;
  }
}

export default TemplateDragDropService;
